import React, { useState, useEffect, useCallback } from "react";
import AddUserModal from "../../components/pages/UserManagementPage/AddUserModal";
import ChangePasswordModal from "../../components/pages/UserManagementPage/ChangePasswordModal";
import {
  getUsers,
  updateUserRole,
  updateUserStatus,
  deleteUser,
  updatePassword,
} from "../../api/userApi";
import "./style.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const ROLES = ["user", "admin", "superadmin"];

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatingRoleId, setUpdatingRoleId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [changePasswordState, setChangePasswordState] = useState({
    isOpen: false,
    user: null,
  });

  const fetchUsers = useCallback(async () => {
    if (searchTerm) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        username: searchTerm || undefined,
      };
      const response = await getUsers(params);
      setUsers(response.data?.items || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data?.total || 0,
      }));
    } catch (err) {
      setError("Không thể tải danh sách người dùng. Vui lòng thử lại.");
      console.error(err);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchTerm]);

  useEffect(() => {
    if (searchTerm !== "") {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchTerm]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [fetchUsers]);

  const handleAddUser = () => {
    fetchUsers();
    setIsModalOpen(false);
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRoleId(userId);
    try {
      await updateUserRole(userId, newRole);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, role: newRole } : user
        )
      );
      MySwal.fire({
        icon: "success",
        title: "Thành công!",
        text: "Cập nhật vai trò thành công.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Có lỗi xảy ra.";
      MySwal.fire({
        icon: "error",
        title: "Lỗi!",
        text: `Cập nhật vai trò thất bại: ${errorMessage}`,
      });
      console.error("Update role error:", err);
    } finally {
      setUpdatingRoleId(null);
    }
  };

  const handleStatusChange = async (userId, currentStatus) => {
    setUpdatingStatusId(userId);
    try {
      await updateUserStatus(userId);
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user._id === userId ? { ...user, status: !currentStatus } : user
        )
      );
      MySwal.fire({
        icon: "success",
        title: "Thành công!",
        text: "Cập nhật trạng thái thành công.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Có lỗi xảy ra.";
      MySwal.fire({
        icon: "error",
        title: "Lỗi!",
        text: `Cập nhật trạng thái thất bại: ${errorMessage}`,
      });
      console.error("Update status error:", err);
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDeleteUser = (userId, username) => {
    MySwal.fire({
      title: "Bạn có chắc không?",
      text: `Bạn sẽ không thể hoàn tác hành động này! Người dùng "${username}" sẽ bị xóa vĩnh viễn.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Đồng ý",
      cancelButtonText: "Hủy",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteUser(userId);
          MySwal.fire(
            "Đã xóa!",
            "Người dùng đã được xóa thành công.",
            "success"
          );
          fetchUsers(); // Refresh the user list
        } catch (err) {
          MySwal.fire("Lỗi!", "Xóa người dùng thất bại.", "error");
          console.error("Delete user error:", err);
        }
      }
    });
  };

  const totalPages = Math.ceil(pagination.total / pagination.pageSize);

  return (
    <>
      <div className="users-header">
        <div className="header-content">
          <h3>Quản lý người dùng</h3>
        </div>
        <div className="header-actions">
          <button
            className="add-user-button"
            onClick={() => setIsModalOpen(true)}
          >
            Thêm mới
          </button>
        </div>
      </div>

      <div className="users-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Tìm kiếm theo tài khoản..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            disabled={searchLoading}
          />
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Đài</th>
              <th>Quyền</th>
              <th>Trạng thái</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="table-message">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan="5" className="table-message error-message">
                  {error}
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="5" className="table-message">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <strong>{user.username}</strong>
                  </td>
                  <td>{user.site}</td>
                  <td>
                    <select
                      className="role-select"
                      value={user.role}
                      onChange={(e) =>
                        handleRoleChange(user._id, e.target.value)
                      }
                      disabled={updatingRoleId === user._id}
                    >
                      {ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      className={`status-toggle-btn ${
                        user.status ? "status-active" : "status-inactive"
                      }`}
                      onClick={() => handleStatusChange(user._id, user.status)}
                      disabled={updatingStatusId === user._id}
                    >
                      {updatingStatusId === user._id
                        ? "..."
                        : user.status
                        ? "Hoạt động"
                        : "Đang khóa"}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn"
                        title="Change Password"
                        onClick={() =>
                          setChangePasswordState({ isOpen: true, user: user })
                        }
                      >
                        🔑
                      </button>
                      <button
                        className="action-btn"
                        title="Delete User"
                        onClick={() =>
                          handleDeleteUser(user._id, user.username)
                        }
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-summary">
          Trang <strong>{pagination.page}</strong> /{" "}
          <strong>{totalPages}</strong> (Tổng: {pagination.total})
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
            disabled={pagination.page <= 1}
          >
            Trước
          </button>
          <button
            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
            disabled={pagination.page >= totalPages}
          >
            Sau
          </button>
        </div>
      </div>

      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddUser={handleAddUser}
      />

      <ChangePasswordModal
        isOpen={changePasswordState.isOpen}
        user={changePasswordState.user}
        onClose={() => setChangePasswordState({ isOpen: false, user: null })}
      />
    </>
  );
};

export default UserManagementPage;
